import { isResource } from '@medplum/core'
import type { Basic, Practitioner, Reference } from '@medplum/fhirtypes'
import { findLastIndex, pick } from 'lodash-es'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  AI_SUGGESTED_EDITS_STAGING_PAYLOAD_SYSTEM,
  BASIC_CODE_SYSTEM,
} from '#src/codeSystems.ts'
import { today } from '#src/dates.ts'
import { Extension } from '#src/decorators.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedBasic } from '#src/models/basic.ts'
import {
  type AcceptReceipt,
  type FieldDecisionStatus,
  isQuestionnaireAnswerOp,
  isValidatedToolOp,
  normalizeStagedCitations,
  type PersistedEditorOperation,
  type PersistedQuestionnaireAnswerOp,
  type PersistedValidatedToolOp,
  questionnaireAnswerKey,
  type StagedSuggestion,
  type SuggestedEditsStaging,
} from '#src/models/compositionNote.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

export type { StagedSuggestion, SuggestedEditsStaging }

// Cap on retained superseded records per (subject, practitioner) staging Basic.
// Superseded records are write-only training data (no in-app path reads them
// back), so we keep only the most-recent window to bound the single valueString
// payload instead of letting it grow unbounded across a long editing session.
const MAX_SUPERSEDED_SUGGESTIONS = 100

/* The two op kinds that compete for a LIVE SLOT — a place only one pending
 * record may occupy, because both selectors read exactly one (`.at(-1)`). Every
 * other op just accumulates and has no slot. */
function liveSlotKey(op: PersistedEditorOperation): string | undefined {
  if (isQuestionnaireAnswerOp(op)) {
    return `questionnaire:${op.questionnaireUrl}`
  }
  if (isValidatedToolOp(op)) {
    return `validated-tool:${op.toolName}`
  }
  return undefined
}

/* The reachable subset of records already treated as pending: one per slot —
 * the LAST, matching the `.at(-1)` both slot getters use — plus every record
 * with no slot. */
function liveBySlot(pending: readonly StagedSuggestion[]): StagedSuggestion[] {
  const liveIdBySlot = new Map<string, string>()
  for (const s of pending) {
    const slot = liveSlotKey(s.op)
    if (slot !== undefined) {
      liveIdBySlot.set(slot, s.id)
    }
  }
  return pending.filter((s) => {
    const slot = liveSlotKey(s.op)
    return slot === undefined || liveIdBySlot.get(slot) === s.id
  })
}

/* Which of `ids` would actually be reachable once every one of them is pending.
 * A record a later run shadowed never arrives in `livePendingSuggestions` —
 * the newer record holds its slot — so a caller that WAITS on a re-pend has to
 * drop those ids or wait for something that can never come. */
export function liveAfterRepend(
  suggestions: readonly StagedSuggestion[],
  ids: readonly string[]
): string[] {
  const repended = new Set(ids)
  const pending = suggestions.filter(
    (s) => s.status === 'pending' || repended.has(s.id)
  )
  return liveBySlot(pending)
    .filter((s) => repended.has(s.id))
    .map((s) => s.id)
}

/* One answer's decision key WITHIN its record's slot — the identity two rounds'
 * proposals are compared on. A questionnaire answer keys by scaffold label
 * (linkId qualified by section and label); a validated-tool answer by bare
 * `linkId`. */
function atomKeysOf(op: PersistedEditorOperation): string[] {
  if (isQuestionnaireAnswerOp(op)) {
    return op.answers.map(questionnaireAnswerKey)
  }
  if (isValidatedToolOp(op)) {
    return op.answers.map((answer) => answer.linkId)
  }
  return []
}

/* Answers rescued off a superseded record, waiting to be folded into the batch
 * record that takes over its slot. A slot is single-kind by construction, so a
 * target only ever inherits the kind it already is. */
type CarriedAnswers =
  | {
      kind: 'questionnaire'
      answers: PersistedQuestionnaireAnswerOp['answers']
    }
  | { kind: 'validated-tool'; answers: PersistedValidatedToolOp['answers'] }

/* The decisions travel with the answers: an atom the clinician already accepted
 * must not come back as an undecided proposal for content the record already
 * holds, and one they rejected must not come back at all. */
interface CarriedAtoms {
  answers: CarriedAnswers
  decisions: Record<string, FieldDecisionStatus>
  edits: Record<string, string>
}

/* What one batch claims for a live slot: every answer key it proposes a value
 * for, and the pending record that will hold the slot (absent when the batch
 * stages only decided records for it, which carry nothing forward). */
interface BatchSlot {
  proposedKeys: Set<string>
  targetId?: string
}

/* Drop one field's clinician-edited answer, and the map once it holds none. */
function clearFieldEdit(suggestion: StagedSuggestion, fieldKey: string) {
  if (!suggestion.fieldEdits) {
    return
  }
  delete suggestion.fieldEdits[fieldKey]
  if (Object.keys(suggestion.fieldEdits).length === 0) {
    delete suggestion.fieldEdits
  }
}

/* Drop one field's decision, and the map itself once it holds none — an absent
 * `fieldDecisions` is how a record with no decisions is stored. */
function clearFieldDecision(suggestion: StagedSuggestion, fieldKey: string) {
  clearFieldEdit(suggestion, fieldKey)
  if (!suggestion.fieldDecisions) {
    return
  }
  delete suggestion.fieldDecisions[fieldKey]
  if (Object.keys(suggestion.fieldDecisions).length === 0) {
    delete suggestion.fieldDecisions
  }
}

/* Everything one re-pend changed, so exactly that can be put back. Held per
 * atom rather than as a record snapshot: the window it spans is a failing
 * network write, and restoring a snapshot would erase every decision the
 * clinician made while it retried. */
export interface RependRestore {
  recordId: string
  atomKey: string
  /* The decision the answer carried before the re-pend cleared it. */
  decision: FieldDecisionStatus
  /* The record's status before this BATCH of re-pends made it pending, so a
   * full restore returns it. Callers re-pending several answers of one record
   * must carry the FIRST capture onto the rest: by the second, the record is
   * already pending because of the first. */
  status: StagedSuggestion['status']
  decidedAt?: string
  /* True when the re-pend COPIED the answer onto this record from an earlier
   * one, so putting it back means taking the copy away again. */
  copied: boolean
}

/* Everything of a superseded record that outlives it: the answers the batch did
 * not re-propose, and the decisions recorded against exactly those. Returns
 * undefined when the batch re-proposed all of them, so nothing is carried. */
function survivorsOf(
  suggestion: StagedSuggestion,
  reProposed: ReadonlySet<string>
): CarriedAtoms | undefined {
  let answers: CarriedAnswers
  let keys: string[]
  if (isQuestionnaireAnswerOp(suggestion.op)) {
    const kept = suggestion.op.answers.filter(
      (answer) => !reProposed.has(questionnaireAnswerKey(answer))
    )
    answers = { kind: 'questionnaire', answers: kept }
    keys = kept.map(questionnaireAnswerKey)
  } else if (isValidatedToolOp(suggestion.op)) {
    const kept = suggestion.op.answers.filter(
      (answer) => !reProposed.has(answer.linkId)
    )
    answers = { kind: 'validated-tool', answers: kept }
    keys = kept.map((answer) => answer.linkId)
  } else {
    return undefined
  }
  if (keys.length === 0) {
    return undefined
  }
  const decisions: Record<string, FieldDecisionStatus> = {}
  Object.assign(decisions, pick(suggestion.fieldDecisions, keys))
  const edits: Record<string, string> = {}
  Object.assign(edits, pick(suggestion.fieldEdits, keys))
  return { answers, decisions, edits }
}

/* Fold rescued answers into the batch record that inherits them. They go before
 * the batch's own, and the batch's own decisions win a key collision — though
 * there can be none, since an answer is only rescued when the batch does not
 * re-propose its key. */
function withCarried(
  suggestion: StagedSuggestion,
  carried: ReadonlyMap<string, CarriedAtoms[]>
): StagedSuggestion {
  const inherited = carried.get(suggestion.id)
  if (!inherited) {
    return suggestion
  }
  const carriedDecisions: Record<string, FieldDecisionStatus> = {}
  const carriedEdits: Record<string, string> = {}
  for (const entry of inherited) {
    Object.assign(carriedDecisions, entry.decisions)
    Object.assign(carriedEdits, entry.edits)
  }
  const fieldDecisions = { ...carriedDecisions, ...suggestion.fieldDecisions }
  const fieldEdits = { ...carriedEdits, ...suggestion.fieldEdits }
  const decided: Pick<StagedSuggestion, 'fieldDecisions' | 'fieldEdits'> = {}
  if (Object.keys(fieldDecisions).length > 0) {
    decided.fieldDecisions = fieldDecisions
  }
  if (Object.keys(fieldEdits).length > 0) {
    decided.fieldEdits = fieldEdits
  }
  if (isQuestionnaireAnswerOp(suggestion.op)) {
    const answers = inherited.flatMap((entry) =>
      entry.answers.kind === 'questionnaire' ? entry.answers.answers : []
    )
    return {
      ...suggestion,
      op: { ...suggestion.op, answers: [...answers, ...suggestion.op.answers] },
      ...decided,
    }
  }
  if (isValidatedToolOp(suggestion.op)) {
    /* `totalScore` and `riskLevel` stay the batch's own. They describe the set
     * the model scored, which is the batch's; no render path reads them off the
     * staged record, and the superseded copy keeps the earlier run's. */
    const answers = inherited.flatMap((entry) =>
      entry.answers.kind === 'validated-tool' ? entry.answers.answers : []
    )
    return {
      ...suggestion,
      op: { ...suggestion.op, answers: [...answers, ...suggestion.op.answers] },
      ...decided,
    }
  }
  return suggestion
}

interface FieldDecisionBuckets {
  pending: string[]
  accepted: string[]
  rejected: string[]
  edited: string[]
}

/**
 * A per-practitioner `Basic` resource that stores AI suggestion staging for a
 * single edited resource — a Composition note, a CarePlan, or any other
 * rich-text surface the AI proposes edits into. The payload is private to the
 * generating practitioner (query is always author-scoped) — another
 * practitioner opening the same resource sees their own empty staging until they
 * run their own generation.
 *
 * Uniqueness key: `(subject: {ResourceType}/{id}, code, author: Practitioner/{practitionerId})`.
 * One `SuggestionStagingBasic` per `(resource, practitioner)` pair holds the
 * full `StagedSuggestion[]` array (one-per-resource-per-practitioner, not
 * per-suggestion). `Basic.subject` is `Reference(Any)`, so the link is not tied
 * to Compositions.
 *
 * The `valueString` JSON payload uses non-throwing safe parsing so malformed
 * persisted state cannot break resource hydration.
 */
export class SuggestionStagingBasic extends ExtendedBasic {
  static code = {
    system: BASIC_CODE_SYSTEM,
    code: 'ai-suggested-edits-staging',
    display: 'AI Suggested Edits Staging',
  }

  static isInstance(value: unknown): value is Basic {
    return (
      isResource(value) &&
      isOfType(value, 'Basic') &&
      hasCodeableConcept(
        value.code,
        codeableConcept(SuggestionStagingBasic.code)
      )
    )
  }

  constructor({
    subject,
    author,
  }: {
    /** The edited resource (Composition, CarePlan, …) this staging belongs to. */
    subject: Reference
    /** The practitioner who generated these suggestions (the owner). */
    author: Reference<Practitioner>
  }) {
    super({
      code: codeableConcept(SuggestionStagingBasic.code),
      created: today(),
      subject,
      author,
    })
  }

  /**
   * Returns a query object that retrieves the staging Basic for the given
   * edited resource AND practitioner. Always author-scoped — never returns
   * another practitioner's staging.
   *
   * @param resourceRef - `{ResourceType}/{id}` reference string (e.g.
   *   `Composition/{id}` or `CarePlan/{id}`)
   * @param practitionerRef - `Practitioner/{practitionerId}` reference string
   */
  static byResourceAndAuthor(
    resourceRef: string,
    practitionerRef: string
  ): QueryTypes {
    return {
      subject: resourceRef,
      code: `${BASIC_CODE_SYSTEM}|ai-suggested-edits-staging`,
      author: practitionerRef,
    }
  }

  /**
   * The raw JSON payload string stored in the extension. Decorated with
   * `@Extension` so `ExtendedResource` serializes/deserializes it automatically.
   */
  @Extension({
    url: AI_SUGGESTED_EDITS_STAGING_PAYLOAD_SYSTEM,
    kind: 'valueString',
  })
  _payloadJson?: string

  /**
   * Parsed staging payload, or `null` when unset or unparseable.
   * Non-throwing: a malformed extension string (round-trip truncation, mid-PATCH
   * race) must not crash the hydration path. Treat it as "no staging" and let the
   * next write heal it (mirrors `CompositionNote.suggestedEditsStaging`).
   */
  private get stagingPayload(): SuggestedEditsStaging | null {
    if (!this._payloadJson) {
      return null
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(this._payloadJson)
    } catch {
      return null
    }
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as SuggestedEditsStaging).suggestions)
    ) {
      return null
    }
    const staging = parsed as SuggestedEditsStaging
    /* Normalizes each record's citation, and heals a corrupt superseded array
     * the same way the `suggestions` guard above heals its own: a non-array is
     * dropped so the next write rewrites a clean sibling, instead of letting
     * `[...superseded]` throw or spread garbage. */
    normalizeStagedCitations(staging)
    return staging
  }

  private set stagingPayload(staging: SuggestedEditsStaging | null) {
    // Persist when there is ANY content worth retaining — live suggestions OR
    // superseded training records. A payload holding only superseded records
    // must NOT be wiped just because `suggestions` went empty.
    const hasContent =
      staging !== null &&
      (staging.suggestions.length > 0 ||
        (staging.supersededSuggestions?.length ?? 0) > 0)
    this._payloadJson = hasContent ? JSON.stringify(staging) : undefined
  }

  /** All LIVE staged suggestions, or an empty array when unset or unparseable. */
  get suggestions(): StagedSuggestion[] {
    return this.stagingPayload?.suggestions ?? []
  }

  /**
   * Records superseded by a later AI run, preserved for AI prompt-tuning.
   * Write-only training data — never surfaced to the clinician; exposed for
   * backend training-data extraction. Empty when unset or unparseable.
   */
  get supersededSuggestions(): StagedSuggestion[] {
    return this.stagingPayload?.supersededSuggestions ?? []
  }

  /** Look up a single staged suggestion by id. */
  getSuggestion(id: string): StagedSuggestion | undefined {
    return this.stagingPayload?.suggestions.find((s) => s.id === id)
  }

  /** The accept receipt for a suggestion, or undefined when none recorded. */
  getReceipt(id: string): AcceptReceipt | undefined {
    return this.stagingPayload?.suggestions.find((s) => s.id === id)?.receipt
  }

  /**
   * Record the accept receipt (R1) for a suggestion BEFORE its side effects run.
   * Idempotent: a receipt already present is left untouched (a resume must not
   * clobber the created-ref map a prior run persisted), and `nodeIds` is only
   * seeded once. No-op when the id is unknown.
   */
  recordAcceptReceipt(id: string, nodeIds: string[]): void {
    const staging = this.stagingPayload
    const suggestion = staging?.suggestions.find((s) => s.id === id)
    if (!staging || !suggestion || suggestion.receipt) {
      return
    }
    suggestion.receipt = { nodeIds, createdRefs: {} }
    this.stagingPayload = staging
  }

  /**
   * Record a created FHIR resource ref on the receipt as it materializes (R1),
   * keyed by the stamp node id. No-op when the id or receipt is unknown.
   */
  recordCreatedRef(id: string, nodeId: string, reference: string): void {
    const staging = this.stagingPayload
    const receipt = staging?.suggestions.find((s) => s.id === id)?.receipt
    if (!staging || !receipt) {
      return
    }
    receipt.createdRefs = { ...receipt.createdRefs, [nodeId]: reference }
    this.stagingPayload = staging
  }

  /**
   * Clear the receipt's recorded refs after compensation (R5), so a later resume
   * never treats a voided resource's ref as still-live. Leaves the receipt (and
   * its nodeIds) in place. No-op when the id or receipt is unknown.
   */
  clearReceiptRefs(id: string): void {
    const staging = this.stagingPayload
    const receipt = staging?.suggestions.find((s) => s.id === id)?.receipt
    if (!staging || !receipt) {
      return
    }
    receipt.createdRefs = {}
    this.stagingPayload = staging
  }

  /*
   * Stage a fresh batch of suggestions. Suggestions ACCUMULATE across AI runs —
   * prompting the AI again never retires review work the clinician has not yet
   * decided on:
   * - KEEP every prior record by default, pending or decided
   *   (`accepted`/`rejected`), from this run or any earlier one. A decided
   *   record persists as the accept/reject signal for training data; a pending
   *   one stays reviewable no matter how many times the AI is re-prompted.
   * - CARRY FORWARD, ANSWER BY ANSWER, a proposal competing for a LIVE SLOT —
   *   a questionnaire-answer record for a `questionnaireUrl` the batch
   *   re-proposes, or a validated-tool record for a `toolName` it re-proposes.
   *   Both surfaces render exactly one pending record per slot, and both decide
   *   ANSWER BY ANSWER (one card per scaffold label; one Accept/Reject per form
   *   field). Superseding on the bare slot key would therefore discard every
   *   answer the clinician had not yet reached, into a `supersededSuggestions`
   *   array no in-app path reads back.
   *   The batch's own answers win their keys; the prior record's other answers,
   *   and the decisions recorded against exactly those, are MOVED INTO the batch
   *   record holding that slot. Merging rather than keeping both is what
   *   preserves the one-live-record-per-slot invariant the two selectors read
   *   by. A prior record for a slot this batch says nothing about is left alone
   *   entirely.
   * - Records whose id reappears in the batch are replaced in place (the batch
   *   record wins), not duplicated and not superseded.
   *
   * Callers must pass run-homogeneous batches — the `supersededByRunId` stamp is
   * read off `batch[0]`. An EMPTY batch proposes nothing, so it can shadow
   * nothing and supersedes nothing.
   */
  setSuggestions(batch: StagedSuggestion[], generatedAt?: string): void {
    const batchRunId = batch[0]?.provenance.runId
    const batchIds = new Set(batch.map((s) => s.id))
    /* Per live slot: the answer keys this batch proposes a value for, and the
     * batch record that will hold the slot. Keys are gathered per slot because
     * an answer key is only unique within one — two questionnaires can both
     * carry a `mobility` item, and two tools both a `mobility` field. The LAST
     * pending batch record for a slot is the carry-forward target, matching the
     * `.at(-1)` rule both selectors read the live record by. */
    const batchSlots = new Map<string, BatchSlot>()
    for (const s of batch) {
      const slotKey = liveSlotKey(s.op)
      if (slotKey === undefined) {
        continue
      }
      const slot = batchSlots.get(slotKey) ?? {
        proposedKeys: new Set<string>(),
      }
      for (const key of atomKeysOf(s.op)) {
        slot.proposedKeys.add(key)
      }
      if (s.status === 'pending') {
        slot.targetId = s.id
      }
      batchSlots.set(slotKey, slot)
    }
    const prior = this.stagingPayload
    const kept: StagedSuggestion[] = []
    const newlySuperseded: StagedSuggestion[] = []
    const carried = new Map<string, CarriedAtoms[]>()

    for (const s of prior?.suggestions ?? []) {
      if (batchIds.has(s.id)) {
        // Re-staged under the same id — the batch record replaces it in place.
        continue
      }
      if (s.status !== 'pending') {
        // A decided record is the training signal; nothing re-targets it.
        kept.push(s)
        continue
      }
      const slotKey = liveSlotKey(s.op)
      const slot = slotKey === undefined ? undefined : batchSlots.get(slotKey)
      if (slot?.targetId === undefined) {
        /* Either the op just accumulates (the inline editor ops), or no pending
         * batch record claims its slot — the batch is for other questionnaires
         * or other tools. Either way this record keeps whatever it holds. */
        kept.push(s)
        continue
      }
      /* The batch record takes the slot, so this one cannot stay live beside it.
       * Its answers move into the batch record instead of dying with it —
       * including the ones the batch says nothing about, which is the point. */
      const survivors = survivorsOf(s, slot.proposedKeys)
      if (survivors) {
        const target = slot.targetId
        carried.set(target, [...(carried.get(target) ?? []), survivors])
      }
      newlySuperseded.push({
        ...s,
        supersededAt: now(),
        supersededByRunId: batchRunId,
      })
    }

    const supersededSuggestions = [
      ...(prior?.supersededSuggestions ?? []),
      ...newlySuperseded,
    ].slice(-MAX_SUPERSEDED_SUGGESTIONS)

    this.stagingPayload = {
      suggestions: [...kept, ...batch.map((s) => withCarried(s, carried))],
      supersededSuggestions,
      generatedAt: generatedAt ?? prior?.generatedAt,
    }
  }

  /**
   * Flip a suggestion's status, stamping `decidedAt` (ISO timestamp) when it
   * becomes `accepted` or `rejected`. No-op when the id is unknown.
   */
  setSuggestionStatus(id: string, status: StagedSuggestion['status']): void {
    const staging = this.stagingPayload
    if (!staging) {
      return
    }
    const suggestion = staging.suggestions.find((s) => s.id === id)
    if (!suggestion) {
      return
    }
    suggestion.status = status
    suggestion.decidedAt = status === 'pending' ? undefined : now()
    this.stagingPayload = staging
  }

  /*
   * The EXPLICIT supersession entry point for inline editor ops: `liveSlotKey`
   * returns undefined for them, so `setSuggestions`' slot-based supersession
   * never reaches them — an inline editor op must be superseded by naming its
   * id directly. A decided record is refused because it is the clinician's
   * answer and the training signal; nothing re-targets it. No-op when there is
   * no staging payload, the id is unknown, or the record is not pending.
   *
   * Returns whether the record was actually superseded, so a caller staging a
   * replacement can refuse to stage it when its target is already decided.
   */
  supersedeSuggestion(id: string, byRunId?: string): boolean {
    const staging = this.stagingPayload
    if (!staging) {
      return false
    }
    const index = staging.suggestions.findIndex((s) => s.id === id)
    if (index === -1) {
      return false
    }
    const suggestion = staging.suggestions[index]
    if (suggestion.status !== 'pending') {
      return false
    }
    staging.suggestions.splice(index, 1)
    const supersededSuggestions = [
      ...(staging.supersededSuggestions ?? []),
      { ...suggestion, supersededAt: now(), supersededByRunId: byRunId },
    ].slice(-MAX_SUPERSEDED_SUGGESTIONS)
    staging.supersededSuggestions = supersededSuggestions
    this.stagingPayload = staging
    return true
  }

  /*
   * Read a field's DURABLE accept/reject decision. `fieldKey` is the caller's
   * decision key: a bare answer `linkId` for a validated tool, and the atom key
   * from `questionnaireAnswerKey` for a questionnaire record — this method never
   * derives it, because only the caller knows which answer atom it holds.
   * Returns `'pending'` when the record is unknown, when it carries no
   * `fieldDecisions` map, or when that key has no entry — so a record staged
   * before per-field decisions existed reads as all-pending.
   */
  getFieldDecision(id: string, fieldKey: string): FieldDecisionStatus {
    const suggestion = this.stagingPayload?.suggestions.find((s) => s.id === id)
    return suggestion?.fieldDecisions?.[fieldKey] ?? 'pending'
  }

  /* The clinician's answer for a field whose decision is `edited`; undefined
   * for any other decision, or an unknown record. */
  getFieldEdit(id: string, fieldKey: string): string | undefined {
    const suggestion = this.stagingPayload?.suggestions.find((s) => s.id === id)
    return suggestion?.fieldEdits?.[fieldKey]
  }

  /*
   * Set (or clear) a field's DURABLE accept/reject decision under the caller's
   * `fieldKey` (see `getFieldDecision`). Writing `'pending'` deletes the entry
   * so the default and an explicit reset stay indistinguishable (and an
   * all-pending record never persists an empty-valued map). The original `op` is
   * left untouched — only the sibling `fieldDecisions` map is mutated, mirroring
   * the `editedContent` annotation pattern. No-op when the id is unknown.
   * `editedValue` is the clinician's answer and is required for `'edited'`;
   * every other decision drops any answer the field carried.
   */
  setFieldDecision(
    id: string,
    fieldKey: string,
    status: FieldDecisionStatus,
    editedValue?: string
  ): void {
    const staging = this.stagingPayload
    if (!staging) {
      return
    }
    const suggestion = staging.suggestions.find((s) => s.id === id)
    if (!suggestion) {
      return
    }
    /* Per-field decisions apply to any op with a decidable answer set —
     * validated tools and care-plan questionnaires alike. Ops with no answers
     * (inline editor ops) have nothing to decide, so they stay out. */
    if (
      !isValidatedToolOp(suggestion.op) &&
      !isQuestionnaireAnswerOp(suggestion.op)
    ) {
      return
    }
    if (status === 'pending') {
      clearFieldDecision(suggestion, fieldKey)
    } else {
      suggestion.fieldDecisions = {
        ...suggestion.fieldDecisions,
        [fieldKey]: status,
      }
      if (status === 'edited') {
        invariant(
          editedValue !== undefined,
          'An edited field decision needs the clinician answer'
        )
        suggestion.fieldEdits = {
          ...suggestion.fieldEdits,
          [fieldKey]: editedValue,
        }
      } else {
        clearFieldEdit(suggestion, fieldKey)
      }
    }
    this.stagingPayload = staging
  }

  /**
   * Set (or clear) a suggestion's island-edited content (design §2.b), keyed by
   * suggestion id. A non-empty string records the edit; `undefined` or an empty
   * string CLEARS it (Restore original) so the record reverts to the AI's
   * proposal and stops counting as a clinician touch. A sibling annotation — the
   * original `op` is never mutated, mirroring `setFieldDecision`. No-op when the
   * id is unknown.
   */
  setEditedContent(id: string, content: string | undefined): void {
    const staging = this.stagingPayload
    if (!staging) {
      return
    }
    const suggestion = staging.suggestions.find((s) => s.id === id)
    if (!suggestion) {
      return
    }
    if (content) {
      suggestion.editedContent = content
    } else {
      delete suggestion.editedContent
    }
    this.stagingPayload = staging
  }

  /**
   * Partition a validated-tool record's answer linkIds by their durable
   * per-field decision. Reads `op.answers[]` as the canonical field set (so a
   * field with no recorded decision lands in `pending`), keying each into
   * `pending` / `accepted` / `rejected` / `edited`. Returns empty arrays when the id
   * is unknown or the record is not a validated-tool op.
   */
  fieldDecisionsByStatus(id: string): FieldDecisionBuckets {
    const empty = { pending: [], accepted: [], rejected: [], edited: [] }
    const suggestion = this.stagingPayload?.suggestions.find((s) => s.id === id)
    if (!suggestion || !isValidatedToolOp(suggestion.op)) {
      return empty
    }
    const buckets: FieldDecisionBuckets = {
      pending: [],
      accepted: [],
      rejected: [],
      edited: [],
    }
    for (const answer of suggestion.op.answers) {
      const status = suggestion.fieldDecisions?.[answer.linkId] ?? 'pending'
      buckets[status].push(answer.linkId)
    }
    return buckets
  }

  /**
   * The PENDING validated-tool suggestion for `toolName`, if any. At most one
   * pending record per tool is ever live: pending records survive across AI
   * runs, but a batch that re-proposes the same tool supersedes the prior one
   * and takes the live slot, so `toolName` still maps to a single pending; the
   * latest match wins if duplicates ever coexist. The validated-tool node view
   * reads this (instead of hydrating a decoration) to drive its banner/prefill.
   */
  pendingValidatedToolSuggestion(
    toolName: string
  ): (StagedSuggestion & { op: PersistedValidatedToolOp }) | undefined {
    const matches = this.stagingPayload?.suggestions.filter(
      (s): s is StagedSuggestion & { op: PersistedValidatedToolOp } =>
        s.status === 'pending' &&
        isValidatedToolOp(s.op) &&
        s.op.toolName === toolName
    )
    return matches?.at(-1)
  }

  /*
   * The PENDING questionnaire-answer record for one canonical questionnaire
   * URL. Same single-live-pending contract as the validated-tool selector: a
   * batch re-proposing the same questionnaire supersedes the prior record, and
   * the latest match wins if duplicates ever coexist.
   */
  pendingQuestionnaireSuggestion(
    questionnaireUrl: string
  ): (StagedSuggestion & { op: PersistedQuestionnaireAnswerOp }) | undefined {
    const matches = this.stagingPayload?.suggestions.filter(
      (s): s is StagedSuggestion & { op: PersistedQuestionnaireAnswerOp } =>
        s.status === 'pending' &&
        isQuestionnaireAnswerOp(s.op) &&
        s.op.questionnaireUrl === questionnaireUrl
    )
    return matches?.at(-1)
  }

  /*
   * Every pending record the clinician can actually reach: the record holding
   * each live slot, plus every pending record that has no slot (the inline
   * editor ops, which all paint).
   *
   * A slot renders through ONE record (`pendingQuestionnaireSuggestion` /
   * `pendingValidatedToolSuggestion`, both `.at(-1)`), so a second pending
   * record for the same slot is invisible. Counting it would report work with
   * no card behind it, and sweeping it would write answers the clinician was
   * never shown. Everything that counts or bulk-decides pending work reads
   * this rather than the raw pending set.
   */
  get livePendingSuggestions(): StagedSuggestion[] {
    return liveBySlot(this.suggestions.filter((s) => s.status === 'pending'))
  }

  /*
   * Where an undo would put a questionnaire answer back: the record that holds
   * the answer now, and the record holding the questionnaire's live slot.
   *
   * The two differ when a later batch took the slot over from a record that had
   * already settled — a settled record is never superseded, so its answers are
   * not carried and it still holds them. Re-pending the answer where it was
   * found would make that record pending again beside the live one, and the
   * surface would show only the live one.
   */
  private resolveQuestionnaireAnswer(
    staging: SuggestedEditsStaging,
    questionnaireUrl: string,
    atomKey: string
  ):
    | {
        holder: StagedSuggestion & { op: PersistedQuestionnaireAnswerOp }
        answer: PersistedQuestionnaireAnswerOp['answers'][number]
        live:
          | (StagedSuggestion & { op: PersistedQuestionnaireAnswerOp })
          | undefined
      }
    | undefined {
    const records = staging.suggestions.filter(
      (s): s is StagedSuggestion & { op: PersistedQuestionnaireAnswerOp } =>
        isQuestionnaireAnswerOp(s.op) &&
        s.op.questionnaireUrl === questionnaireUrl
    )
    const held = records.flatMap((s) => {
      const answer = s.op.answers.find(
        (a) => questionnaireAnswerKey(a) === atomKey
      )
      return answer ? [{ holder: s, answer }] : []
    })
    const holding = held.at(-1)
    if (!holding) {
      return undefined
    }
    return {
      ...holding,
      live: records.filter((s) => s.status === 'pending').at(-1),
    }
  }

  /*
   * Whether an undo can put this questionnaire answer back on the surface —
   * asked BEFORE the compensating write, so an accept is never reversed out of
   * the clinical record with no card left to accept it again. False once no
   * record in this staging holds the answer at all (it was cleared, or this is
   * another practitioner's).
   */
  canRependQuestionnaireAnswer(
    questionnaireUrl: string,
    atomKey: string
  ): boolean {
    const staging = this.stagingPayload
    return (
      !!staging &&
      this.resolveQuestionnaireAnswer(staging, questionnaireUrl, atomKey) !==
        undefined
    )
  }

  /*
   * Return one questionnaire answer to undecided, so its card comes back.
   *
   * The decision is cleared on the record holding the questionnaire's LIVE
   * slot, which is where the surface reads its cards from. When the answer sits
   * on an earlier record that settled before a later batch took the slot, it is
   * COPIED across rather than moved: the earlier record keeps the proposal it
   * made and the decision it recorded, which is the training signal, while the
   * live record grows the undecided copy the clinician can act on. Only one of
   * the two is ever pending, so only one is ever rendered.
   *
   * No-op when no live record holds the answer — `canRependQuestionnaireAnswer`
   * is the caller's guard for that.
   */
  rependQuestionnaireAnswer(
    questionnaireUrl: string,
    atomKey: string
  ): RependRestore | undefined {
    const staging = this.stagingPayload
    if (!staging) {
      return undefined
    }
    const resolved = this.resolveQuestionnaireAnswer(
      staging,
      questionnaireUrl,
      atomKey
    )
    if (!resolved) {
      return undefined
    }
    const { holder, answer, live } = resolved
    const target = live ?? holder
    const copied = target !== holder
    const restore: RependRestore = {
      recordId: target.id,
      atomKey,
      decision: target.fieldDecisions?.[atomKey] ?? 'pending',
      status: target.status,
      decidedAt: target.decidedAt,
      copied,
    }
    if (copied) {
      target.op.answers = [...target.op.answers, answer]
    }
    clearFieldDecision(target, atomKey)
    // An undecided answer makes its record pending by definition.
    target.status = 'pending'
    target.decidedAt = undefined
    this.stagingPayload = staging
    return restore
  }

  /*
   * Put back exactly what one `rependQuestionnaireAnswer` changed, and nothing
   * else. Used when the compensating answer write an undo re-pended FOR then
   * fails: without it the card would stand beside an answer still holding the
   * accepted value, describing a decision the clinician no longer has.
   *
   * Keyed to the one atom rather than restoring a snapshot of the record — the
   * interval it spans is a failing network write, and the clinician goes on
   * deciding other answers while it retries. No-op when the record is gone.
   */
  restoreRepend(restore: RependRestore): void {
    const staging = this.stagingPayload
    const target = staging?.suggestions.find((s) => s.id === restore.recordId)
    if (!staging || !target || !isQuestionnaireAnswerOp(target.op)) {
      return
    }
    const op = target.op
    let answers = op.answers
    if (restore.copied) {
      // The copy the re-pend appended, not an earlier answer sharing its key.
      const appended = findLastIndex(
        op.answers,
        (answer) => questionnaireAnswerKey(answer) === restore.atomKey
      )
      answers = [...op.answers]
      if (appended >= 0) {
        answers.splice(appended, 1)
      }
      target.op = { ...op, answers }
    }
    if (restore.decision === 'pending') {
      clearFieldDecision(target, restore.atomKey)
    } else {
      target.fieldDecisions = {
        ...target.fieldDecisions,
        [restore.atomKey]: restore.decision,
      }
    }
    /* The record's status is DERIVED, never replayed: restoring some of a
     * batch's atoms leaves the rest undecided, and an undecided answer makes
     * its record pending whatever it was before. Only a restore that leaves
     * nothing undecided returns the status the batch found. */
    const undecided = answers.some(
      (answer) =>
        (target.fieldDecisions?.[questionnaireAnswerKey(answer)] ??
          'pending') === 'pending'
    )
    target.status = undecided ? 'pending' : restore.status
    target.decidedAt = undecided ? undefined : restore.decidedAt
    this.stagingPayload = staging
  }
}

/*
 * The content a staged suggestion would ACTUALLY insert: the clinician's island
 * edit when the record carries one, otherwise the AI op's own proposed HTML.
 * The proposed HTML is supplied by the caller because deriving it needs the
 * editor-side op union, which this package does not depend on.
 *
 * The accept pipeline inserts exactly this, so every surface that DISPLAYS,
 * SEEDS, or COMPARES a suggestion's content must read it here. A card painted
 * from the op while an accept would insert the edit is a preview that lies about
 * what it will write into the note; a presence check asked of the op is asked
 * about content the accept never inserted.
 */
export function effectiveSuggestionHtml(
  record: Pick<StagedSuggestion, 'editedContent'> | undefined,
  proposedHtml: string
): string
export function effectiveSuggestionHtml(
  record: Pick<StagedSuggestion, 'editedContent'> | undefined,
  proposedHtml: string | undefined
): string | undefined
export function effectiveSuggestionHtml(
  record: Pick<StagedSuggestion, 'editedContent'> | undefined,
  proposedHtml: string | undefined
): string | undefined {
  return record?.editedContent ?? proposedHtml
}

/*
 * The validated-tool record a node view REVIEWS for `toolName`: the live slot,
 * WHATEVER its status — `pendingValidatedToolSuggestion` without the pending
 * filter, and the same "latest wins" rule.
 *
 * A record settles as soon as every field is decided, which happens BEFORE the
 * clinician submits the form. Reading only pending records would withdraw the
 * prefill at that moment — blanking the values they had just accepted — and
 * take away the Undo that has to outlive the decision. The submitted response,
 * not the record's status, is what ends the review.
 */
export function latestValidatedToolSuggestion(
  suggestions: readonly StagedSuggestion[],
  toolName: string
): (StagedSuggestion & { op: PersistedValidatedToolOp }) | undefined {
  return suggestions
    .filter(
      (s): s is StagedSuggestion & { op: PersistedValidatedToolOp } =>
        isValidatedToolOp(s.op) && s.op.toolName === toolName
    )
    .at(-1)
}

/*
 * Derive the record-level status a suggestion should hold GIVEN its per-field
 * decisions.
 *
 * The rule:
 * - Empty `fieldKeys` (nothing to decide) → `'pending'`.
 * - ANY field still pending → `'pending'`.
 * - Every field decided and ALL rejected → `'rejected'`.
 * - Every field decided with at least one accepted → `'accepted'`, a MIXED
 *   outcome included.
 *
 * The record is finished once the clinician owes no more decisions on it, which
 * is what the pending-work count reads. Submitting the form is a separate,
 * CLINICAL commitment (it creates the QuestionnaireResponse) and does not gate
 * this: gating on submit left the status bar counting a record whose fields were
 * all decided and whose highlights were all gone.
 *
 * Pure: takes the record's decision keys — bare answer linkIds for a validated
 * tool, atom keys from `questionnaireAnswerKey` for a questionnaire — plus the
 * decisions map, and returns the derived status.
 */
export function deriveRecordStatusFromFieldDecisions(
  fieldKeys: string[],
  fieldDecisions: Record<string, FieldDecisionStatus> | undefined
): 'pending' | 'accepted' | 'rejected' {
  if (
    fieldKeys.length === 0 ||
    fieldKeys.some(
      (fieldKey) => (fieldDecisions?.[fieldKey] ?? 'pending') === 'pending'
    )
  ) {
    return 'pending'
  }
  return fieldKeys.every(
    (fieldKey) => fieldDecisions?.[fieldKey] === 'rejected'
  )
    ? 'rejected'
    : 'accepted'
}
